import { useContext, useState } from "react";
import { Box, Button, Text, useToast } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { CartContext } from "../../../Context/CartContext";
import { BoozenToast } from "../../BoozenToast";
import { collectSelectedItems, groupResults, summarizeBlockStates } from "../helpers";
import { CART_BLOCKED_CATEGORIES } from "../constants";
import { ResultGroup } from "./ResultGroup";
import { ResultBlock } from "./ResultBlock";
import { OrderSummary } from "./OrderSummary";

export function ResultsScreen({ calcResult, itemQuantities, setItemQuantities, onAdjust }) {
  const { handleBulkAddToCart } = useContext(CartContext);
  const navigate = useNavigate();
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);

  const { context, results } = calcResult;
  const groups = groupResults(results);

  const selectedItems = collectSelectedItems(results, itemQuantities);
  const totalValue = selectedItems.reduce((sum, i) => sum + i.amount, 0);
  const totalUnits = selectedItems.reduce((sum, i) => sum + i.quantity, 0);
  const { readyTotal, readyComplete, pendingLabels, unavailableLabels } = summarizeBlockStates(
    results,
    itemQuantities
  );

  // Correction C12 (handoff v5): destilados are still cadastro'd as "Caixa"
  // in the catalog, so the quantity the API returns for that category would
  // get multiplied by the batch's Qtd/caixa at checkout — excluded from the
  // cart payload until the backend confirms the catalog fix. They still
  // count toward the on-screen total so the user sees the full recommendation.
  const cartableItems = selectedItems.filter((i) => !CART_BLOCKED_CATEGORIES.includes(i.resultKey));
  const blockedItems = selectedItems.filter((i) => CART_BLOCKED_CATEGORIES.includes(i.resultKey));

  async function handleAddAllToCart() {
    const items = cartableItems.map(({ product_id, product_name, batch_id, amount, quantity }) => ({
      product_id,
      product_name,
      batch_id,
      quantity,
      amount,
    }));

    if (items.length === 0) return;

    setSubmitting(true);
    try {
      await handleBulkAddToCart(items);
      if (blockedItems.length > 0) {
        const names = [...new Set(blockedItems.map((i) => i.product_name))].join(", ");
        toast({
          position: "top",
          duration: 8000,
          isClosable: true,
          render: ({ onClose }) => (
            <BoozenToast
              type="info"
              title="Alguns itens ficaram de fora"
              description={`${names}: ainda não é possível adicionar automaticamente. Inclua manualmente no carrinho.`}
              onClose={onClose}
            />
          ),
        });
      }
      navigate("/carrinho");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Box>
      <Text fontSize={{ base: "20px", md: "24px" }} fontWeight="900" color="#004a33" mb="2px">
        Monte seu pedido
      </Text>
      <Text fontSize="12px" fontWeight="700" color="rgba(0,74,51,.55)" mb="18px">
        {context.people} pessoas · {context.event_type_label} · {context.duration_label}
      </Text>

      <Box display="flex" flexDirection="column" gap="16px" mb="22px">
        {groups.map((g) =>
          g.type === "group" ? (
            <Box key={g.groupKey}>
              <ResultGroup
                groupKey={g.groupKey}
                items={g.items}
                itemQuantities={itemQuantities}
                setItemQuantities={setItemQuantities}
              />
            </Box>
          ) : (
            <ResultBlock
              key={g.items[0].key}
              result={g.items[0]}
              itemQuantities={itemQuantities}
              setItemQuantities={setItemQuantities}
            />
          )
        )}
      </Box>

      <Box mb="14px">
        <OrderSummary
          totalValue={totalValue}
          totalUnits={totalUnits}
          peopleCount={context.people}
          readyTotal={readyTotal}
          readyComplete={readyComplete}
          pendingLabels={pendingLabels}
          unavailableLabels={unavailableLabels}
        />
      </Box>

      <Box display="flex" flexDirection="column" gap="8px">
        <Button
          h="50px"
          borderRadius="14px"
          bg="#004a33"
          color="#fc8f04"
          fontWeight="900"
          fontSize="14px"
          isDisabled={cartableItems.length === 0}
          isLoading={submitting}
          onClick={handleAddAllToCart}
          _hover={{ opacity: 0.92 }}
        >
          Adicionar tudo ao carrinho
        </Button>
        <Button variant="ghost" color="#004a33" fontWeight="700" fontSize="13px" onClick={onAdjust}>
          Ajustar escolhas
        </Button>
      </Box>
    </Box>
  );
}
